<template>
  <div class="modal-card">
    <b-loading :isFullPage="false" :active="isLoading" />
    <header class="modal-card-head">
      <span class="modal-card-title is-size-5 has-text-centered">{{
        isGiveKnowledge
          ? General.EXTERNAL_GIVE_SCIENCE
          : General.REQUEST_SIGNATURE_EXTERNE
      }}</span>
      <div class="close-button cursor-pointer" @click.stop="$emit('close')">
        <b-icon icon="close" />
      </div>
    </header>

    <ValidationObserver
      ref="externalRequestSignature"
      class="modal-content-container"
      tag="section"
    >
      <section class="spacing-p-10">
        <ValidationProvider
          v-if="!isGiveKnowledge"
          v-slot="{ errors }"
          :rules="`required|${cpf_cnpj.length > 14 ? 'cnpj' : 'cpf'}`"
          immediate
          class="column is-6-desktop is-paddingless spacing-ml-10"
          tag="div"
        >
          <b-field
            :key="`cpf_${keyForm}`"
            :label="General.CPF_CNPJ"
            :type="errors.length ? 'is-refused' : ''"
            customClass="has-text-color-7"
          >
            <b-input
              v-model="cpf_cnpj"
              v-mask="maskCGC"
              :placeholder="App.ExternalAssign.DIGITE_CGC_ASSIGN"
            />
            <template v-if="errors.length" #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors }"
          :rules="
            ['emptyField', General.PLACEHOLDER.TYPING_CORPORATE_NAME].join(':')
          "
          immediate
          class="column is-paddingless spacing-ml-10 my-5"
          tag="div"
        >
          <b-field
            :key="`name_${keyForm}`"
            :label="General.NAME_SOCIAL_REASON"
            :type="errors.length ? 'is-refused' : ''"
            customClass="has-text-color-7"
          >
            <b-input
              v-model="name_social_reason"
              :placeholder="General.PLACEHOLDER.TYPING_CORPORATE_NAME"
            />
            <template v-if="errors.length" #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors }"
          :rules="['emptyField', General.PLACEHOLDER.TYPE_THE_MAIL].join(':')"
          class="column is-paddingless spacing-ml-10"
          tag="div"
        >
          <b-field
            :key="`mail_${keyForm}`"
            :label="General.EMAIL"
            :type="errors.length ? 'is-refused' : ''"
            customClass="has-text-color-7"
          >
            <b-input
              v-model="email"
              :placeholder="
                isGiveKnowledge
                  ? General.PLACEHOLDER.TYPE_THE_MAIL
                  : App.ExternalAssign.DIGITE_MAIL_ASSIGN
              "
            />
            <template v-if="errors.length" #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>
      </section>

      <footer class="modal-card-foot is-justify-content-flex-end">
        <div class="buttons">
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            @click="includeAssign"
          >
            {{
              (edit && edit.id ? General.EDIT : General.INCLUDE).toUpperCase()
            }}
          </b-button>
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            class="is-outlined"
            @click="$emit('close')"
          >
            {{ General.CANCEL.toUpperCase() }}
          </b-button>
        </div>
      </footer>
    </ValidationObserver>
  </div>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import { mapGetters } from "vuex";
import { App, General } from "@/utils/constants";
import { CPForCNPJ } from "@/utils/masks";

export default {
  name: "ModalExternalAssign",

  props: {
    edit: {
      type: Object,
      default: () => {}
    },
    isGiveKnowledge: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function,
      default: () => null
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      name_social_reason: "",
      cpf_cnpj: "",
      email: "",
      keyForm: Date.now()
    };
  },

  computed: {
    ...mapGetters({
      isContrast: "isContrast"
    }),
    maskCGC() {
      return this.cpf_cnpj.length > 14
        ? "##.###.###/####-##"
        : "###.###.###-###";
    },
    cpfCnpjCleared() {
      return this.cpf_cnpj.replace(/\D/g, "");
    }
  },

  beforeMount() {
    if (this.edit?.id) {
      this.name_social_reason = this.edit.name;
      this.cpf_cnpj = CPForCNPJ(this.edit.cpf_cnpj);
      this.email = this.edit.email;
    }
  },

  mounted() {
    this.$refs.externalRequestSignature.reset();
  },

  methods: {
    close() {
      this.$parent.close();
    },
    async includeAssign() {
      const fails = [];
      const valid = await this.$refs.externalRequestSignature.validate();
      this.keyForm = Date.now();
      if (!valid) return;
      if (this.cpfCnpjCleared.trim() === "" && !this.isGiveKnowledge)
        fails.push(App.ExternalAssign.DIGITE_CGC_ASSIGN);
      if (this.name_social_reason.trim() === "")
        fails.push(App.ExternalAssign.DIGITE_NAME_ASSIGN);
      if (this.email.trim() === "")
        fails.push(App.ExternalAssign.DIGITE_MAIL_ASSIGN);

      if (fails.length) {
        this.snackToast.open({
          message: `${fails.join(", ")}.`,
          position: "is-top",
          type: "is-danger",
          duration: 8000
        });
      } else {
        this.isLoading = true;
        this.onClose({
          id: this.edit?.id || uuidv4(),
          name: this.name_social_reason,
          cpf_cnpj: this.cpfCnpjCleared,
          email: this.email,
          isExternal: true
        });
        this.isLoading = false;
        this.close();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  display: flex;
  justify-content: space-between;
  min-height: 300px;
  max-height: 75vh;
  background-color: $color-1;
}

.header-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-bottom: 1px solid;
  border-bottom-color: #cdcdcd;
}

.card-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid;
  border-bottom-color: #cdcdcd;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
}
</style>
